import { openJson, sealJson } from './crypto';
import type { ReadPolicy } from './policy';
import { offlineStore } from './store';
import { currentDataKey } from './vault';

/**
 * The offline copy of what the operator has read.
 *
 * Every record is sealed with the vault's data key, so the payloads are unreadable without the
 * password (or, while signed in, the device key). What is kept in the clear is an index: which
 * paths are held, for whom, how big they are and when they were taken. None of that is a
 * figure from anyone's books, and keeping it readable is what lets the device report and prune
 * its own storage without unsealing a thing.
 */

const INDEX_KEY = 'cache-index';

/** A copy older than this is dropped: it is well past the window the vault itself allows. */
const RETENTION_DAYS = 30;

/** Ceilings so a long-lived install cannot grow without bound on a small device. */
const MAX_ENTRIES = 600;
const MAX_BYTES = 24 * 1024 * 1024;

interface CacheRecord {
  readonly version: 1;
  readonly storedAt: string;
  readonly sealed: string;
}

interface IndexEntry {
  readonly key: string;
  readonly datasetKey: string;
  readonly storedAt: string;
  readonly bytes: number;
}

interface CacheIndex {
  readonly entries: readonly IndexEntry[];
}

export interface CachedRead<T> {
  readonly data: T;
  readonly storedAt: string;
}

export interface CacheStatistics {
  readonly entries: number;
  readonly bytes: number;
  readonly oldestAt: string | null;
  readonly newestAt: string | null;
  readonly datasets: readonly { readonly datasetKey: string; readonly entries: number; readonly bytes: number }[];
}

/**
 * The identity of a cached read.
 *
 * The owner comes first so that everything belonging to one organization sorts together, and a
 * tenant-scoped read is never handed to a different tenant however similar its path.
 */
export function cacheKey(policy: ReadPolicy, owner: string, path: string): string {
  return `${policy.scope}:${owner}:${path}`;
}

async function readIndex(): Promise<CacheIndex> {
  return await offlineStore().get<CacheIndex>('meta', INDEX_KEY) ?? { entries: [] };
}

function writeIndex(index: CacheIndex): Promise<void> {
  return offlineStore().put('meta', INDEX_KEY, index);
}

/**
 * Drops the copies that are too old, and then the oldest of what is left until the entry and
 * byte ceilings are met.
 */
async function prune(index: CacheIndex): Promise<CacheIndex> {
  const cutoff = Date.now() - RETENTION_DAYS * 24 * 60 * 60 * 1000;
  const fresh = index.entries.filter((entry) => Date.parse(entry.storedAt) >= cutoff);
  const expired = index.entries.filter((entry) => Date.parse(entry.storedAt) < cutoff);

  // Newest first, so trimming from the end always removes the least recently refreshed copy.
  const ordered = [...fresh].sort((left, right) => right.storedAt.localeCompare(left.storedAt));
  const kept: IndexEntry[] = [];
  const dropped = [...expired];
  let bytes = 0;

  for (const entry of ordered) {
    if (kept.length >= MAX_ENTRIES || bytes + entry.bytes > MAX_BYTES) {
      dropped.push(entry);
      continue;
    }
    kept.push(entry);
    bytes += entry.bytes;
  }

  for (const entry of dropped) await offlineStore().remove('cache', entry.key);
  return { entries: kept };
}

/**
 * Files a response on the device.
 *
 * Never awaited on the request path by the caller: a device that cannot write its copy must not
 * make a successful online read look like a failure.
 */
export async function writeCachedRead(policy: ReadPolicy, owner: string, path: string, data: unknown): Promise<void> {
  const key = currentDataKey();
  if (key === null) return;

  const cacheEntryKey = cacheKey(policy, owner, path);
  const sealed = await sealJson(key, data);
  const record: CacheRecord = { version: 1, storedAt: new Date().toISOString(), sealed };
  await offlineStore().put('cache', cacheEntryKey, record);

  const index = await readIndex();
  const next: IndexEntry = {
    key: cacheEntryKey,
    datasetKey: policy.datasetKey,
    storedAt: record.storedAt,
    bytes: sealed.length,
  };
  await writeIndex(await prune({
    entries: [...index.entries.filter((entry) => entry.key !== cacheEntryKey), next],
  }));
}

/**
 * Reads a copy back.
 *
 * Age is reported rather than enforced. Refusing to show a two-day-old trial balance to someone
 * with no connection helps nobody; showing it under a plain statement of when it was taken is
 * what lets them decide whether to trust it.
 */
export async function readCachedRead<T>(policy: ReadPolicy, owner: string, path: string): Promise<CachedRead<T> | null> {
  const key = currentDataKey();
  if (key === null) return null;

  const record = await offlineStore().get<CacheRecord>('cache', cacheKey(policy, owner, path));
  if (record === undefined) return null;

  try {
    return { data: await openJson<T>(key, record.sealed), storedAt: record.storedAt };
  } catch {
    // Sealed under a superseded data key — a re-enrolment. It is unreadable now, so treat it as
    // absent and let it fall out of the index on the next prune.
    return null;
  }
}

/** What the device panel reports: how much is held, how old it is, and under which headings. */
export async function cacheStatistics(): Promise<CacheStatistics> {
  const { entries } = await readIndex();
  const byDataset = new Map<string, { entries: number; bytes: number }>();
  let bytes = 0;

  for (const entry of entries) {
    bytes += entry.bytes;
    const bucket = byDataset.get(entry.datasetKey) ?? { entries: 0, bytes: 0 };
    bucket.entries += 1;
    bucket.bytes += entry.bytes;
    byDataset.set(entry.datasetKey, bucket);
  }

  const timestamps = entries.map((entry) => entry.storedAt).sort();
  return {
    entries: entries.length,
    bytes,
    oldestAt: timestamps[0] ?? null,
    newestAt: timestamps.at(-1) ?? null,
    datasets: [...byDataset.entries()]
      .map(([datasetKey, bucket]) => ({ datasetKey, entries: bucket.entries, bytes: bucket.bytes }))
      .sort((left, right) => right.bytes - left.bytes),
  };
}

/** Removes every cached read, leaving the vault and the queue alone. */
export async function clearCachedReads(): Promise<void> {
  await offlineStore().clear('cache');
  await writeIndex({ entries: [] });
}
